import { useState } from "react";
import { Phone, Send, CalendarDays, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { PHONE_DISPLAY, PHONE_HREF } from "@/lib/contact";
import { submitEnquiry } from "@/lib/api";

const BIN_OPTIONS = [
    { value: "2m3", label: "2m³ Mini Skip" },
    { value: "3m3", label: "3m³ Skip" },
    { value: "4m3", label: "4m³ Skip" },
    { value: "6m3", label: "6m³ Skip" },
    { value: "8m3", label: "8m³ Skip" },
    { value: "Not sure", label: "Not sure — help me pick" },
];

const WASTE_OPTIONS = [
    { value: "general and household", label: "General & household" },
    { value: "green and garden", label: "Green & garden" },
    { value: "mixed heavy", label: "Mixed heavy (soil, bricks, concrete)" },
    { value: "construction", label: "Construction" },
    { value: "commercial", label: "Commercial" },
];

const emptyForm = {
    name: "",
    phone: "",
    email: "",
    suburb: "",
    binSize: "",
    wasteType: "",
    preferredDate: null,
    message: "",
};

export const QuoteForm = () => {
    const [form, setForm] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const setField = (name, value) => {
        setForm((f) => ({ ...f, [name]: value }));
        setErrors((e) => ({ ...e, [name]: undefined }));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim() || form.name.trim().length < 2)
            next.name = "Please tell us your name.";
        if (!form.phone.trim() || form.phone.trim().length < 6)
            next.phone = "Please enter a contact number.";
        else if (!/^[0-9 +()\-]{6,20}$/.test(form.phone.trim()))
            next.phone = "Please enter a valid phone number.";
        if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email.trim()))
            next.email = "Please enter a valid email.";
        if (!form.suburb.trim()) next.suburb = "Please enter your suburb or postcode.";
        if (!form.binSize) next.binSize = "Please choose a bin size.";
        if (!form.wasteType) next.wasteType = "Please choose a waste type.";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                suburb: form.suburb.trim(),
                binSize: form.binSize,
                wasteType: form.wasteType,
                preferredDate: form.preferredDate
                    ? format(form.preferredDate, "yyyy-MM-dd")
                    : null,
                message: form.message.trim() || null,
            };
            const res = await submitEnquiry(payload);
            setSuccess(true);
            toast.success(
                res?.emailed
                    ? "Thanks — we’ve got your enquiry and will be in touch shortly."
                    : "Thanks — we’ve saved your enquiry. If you don’t hear back within an hour, call us on " +
                          PHONE_DISPLAY +
                          ".",
            );
            setForm(emptyForm);
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Something went wrong sending your enquiry. Please call " +
                    PHONE_DISPLAY +
                    ".";
            toast.error(typeof detail === "string" ? detail : "Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="quote" data-testid="section-quote" className="py-14 md:py-20 bg-tp-offwhite">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid gap-8 lg:grid-cols-12">
                    {/* Left: copy + call CTA */}
                    <div className="lg:col-span-5 tp-reveal">
                        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-tp-forest font-semibold">
                            <span className="h-1.5 w-1.5 rounded-full bg-tp-gold" />
                            Get a quote
                        </div>
                        <h2 className="mt-3 font-heading text-3xl md:text-4xl text-tp-forest leading-tight">
                            Fast quote by phone — or fill in the form
                        </h2>
                        <p className="mt-3 text-sm md:text-base text-tp-ink/80">
                            For the fastest quote, call{" "}
                            <a
                                href={PHONE_HREF}
                                className="font-semibold text-tp-forest underline underline-offset-4 decoration-tp-gold decoration-2 hover:text-tp-forest"
                                data-testid="quote-inline-call"
                            >
                                {PHONE_DISPLAY}
                            </a>
                            . Or send your details and we’ll get straight back to you.
                        </p>

                        <div className="mt-5 rounded-2xl bg-tp-forest text-white p-5 md:p-6 border border-tp-forest/60">
                            <div className="text-xs uppercase tracking-[0.16em] text-tp-gold">Prefer to call?</div>
                            <p className="mt-1 font-heading text-2xl leading-tight">
                                We’ll help you pick the right bin in under a minute.
                            </p>
                            <a
                                href={PHONE_HREF}
                                data-testid="quote-form-call-button"
                                className="tp-btn-primary w-full justify-center mt-4 h-12 text-base tp-focus"
                            >
                                <Phone size={18} strokeWidth={2.6} />
                                Call {PHONE_DISPLAY}
                            </a>
                            <ul className="mt-4 space-y-1.5 text-xs text-white/85">
                                <li>• Family owned & local Sydney business</li>
                                <li>• Same day or next day where available</li>
                                <li>• Transparent pricing, no hidden fees</li>
                                <li>• Fully insured on every job</li>
                            </ul>
                        </div>
                    </div>

                    {/* Right: form */}
                    <div className="lg:col-span-7 tp-reveal">
                        <div
                            data-testid="quote-form-container"
                            className="bg-white rounded-2xl p-5 md:p-7 border border-tp-forest/10 shadow-[0_18px_50px_rgba(11,27,18,0.10)]"
                        >
                            {success ? (
                                <div
                                    data-testid="quote-form-success-message"
                                    className="rounded-xl border border-tp-recycle/40 bg-tp-recycle/10 p-5"
                                >
                                    <div className="flex items-center gap-2">
                                        <CheckCircle2 size={22} className="text-tp-recycle" />
                                        <h3 className="font-heading text-2xl text-tp-forest">
                                            Thanks — we’ve got your enquiry
                                        </h3>
                                    </div>
                                    <p className="mt-2 text-sm text-tp-ink/85">
                                        We’ll get straight back to you. If it’s urgent,
                                        call{" "}
                                        <a
                                            href={PHONE_HREF}
                                            className="font-semibold text-tp-forest underline underline-offset-4 decoration-tp-gold decoration-2"
                                            data-testid="success-call-link"
                                        >
                                            {PHONE_DISPLAY}
                                        </a>
                                        .
                                    </p>
                                    <div className="mt-4 flex flex-col sm:flex-row gap-3">
                                        <a
                                            href={PHONE_HREF}
                                            data-testid="success-call-button"
                                            className="tp-btn-primary text-sm h-11 justify-center tp-focus"
                                        >
                                            <Phone size={16} strokeWidth={2.6} />
                                            Call {PHONE_DISPLAY}
                                        </a>
                                        <button
                                            type="button"
                                            onClick={() => setSuccess(false)}
                                            data-testid="success-new-enquiry"
                                            className="tp-btn-ghost text-sm h-11 justify-center border border-tp-forest/15 tp-focus"
                                        >
                                            Send another enquiry
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <form
                                    onSubmit={handleSubmit}
                                    noValidate
                                    data-testid="quote-form"
                                    className="grid gap-4 sm:grid-cols-2"
                                >
                                    {/* Name */}
                                    <div className="sm:col-span-1">
                                        <label
                                            htmlFor="tps-name"
                                            className="block text-sm font-semibold text-tp-forest mb-1"
                                        >
                                            Name
                                        </label>
                                        <input
                                            id="tps-name"
                                            data-testid="quote-form-field-name"
                                            type="text"
                                            autoComplete="name"
                                            value={form.name}
                                            onChange={(e) => setField("name", e.target.value)}
                                            className={`w-full h-11 rounded-lg border ${
                                                errors.name
                                                    ? "border-red-500"
                                                    : "border-tp-forest/20"
                                            } bg-white px-3 text-sm text-tp-ink focus:outline-none focus:ring-2 focus:ring-tp-gold focus:border-tp-gold`}
                                            placeholder="Full name"
                                            aria-invalid={!!errors.name}
                                            aria-describedby={errors.name ? "tps-name-err" : undefined}
                                        />
                                        {errors.name && (
                                            <p id="tps-name-err" className="mt-1 text-xs text-red-600">
                                                {errors.name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Phone */}
                                    <div className="sm:col-span-1">
                                        <label
                                            htmlFor="tps-phone"
                                            className="block text-sm font-semibold text-tp-forest mb-1"
                                        >
                                            Phone
                                        </label>
                                        <input
                                            id="tps-phone"
                                            data-testid="quote-form-field-phone"
                                            type="tel"
                                            inputMode="tel"
                                            autoComplete="tel"
                                            value={form.phone}
                                            onChange={(e) => setField("phone", e.target.value)}
                                            className={`w-full h-11 rounded-lg border ${
                                                errors.phone
                                                    ? "border-red-500"
                                                    : "border-tp-forest/20"
                                            } bg-white px-3 text-sm text-tp-ink focus:outline-none focus:ring-2 focus:ring-tp-gold focus:border-tp-gold`}
                                            placeholder="04xx xxx xxx"
                                            aria-invalid={!!errors.phone}
                                            aria-describedby={errors.phone ? "tps-phone-err" : undefined}
                                        />
                                        {errors.phone && (
                                            <p id="tps-phone-err" className="mt-1 text-xs text-red-600">
                                                {errors.phone}
                                            </p>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div className="sm:col-span-1">
                                        <label
                                            htmlFor="tps-email"
                                            className="block text-sm font-semibold text-tp-forest mb-1"
                                        >
                                            Email
                                        </label>
                                        <input
                                            id="tps-email"
                                            data-testid="quote-form-field-email"
                                            type="email"
                                            inputMode="email"
                                            autoComplete="email"
                                            value={form.email}
                                            onChange={(e) => setField("email", e.target.value)}
                                            className={`w-full h-11 rounded-lg border ${
                                                errors.email
                                                    ? "border-red-500"
                                                    : "border-tp-forest/20"
                                            } bg-white px-3 text-sm text-tp-ink focus:outline-none focus:ring-2 focus:ring-tp-gold focus:border-tp-gold`}
                                            placeholder="you@example.com"
                                            aria-invalid={!!errors.email}
                                            aria-describedby={errors.email ? "tps-email-err" : undefined}
                                        />
                                        {errors.email && (
                                            <p id="tps-email-err" className="mt-1 text-xs text-red-600">
                                                {errors.email}
                                            </p>
                                        )}
                                    </div>

                                    {/* Suburb */}
                                    <div className="sm:col-span-1">
                                        <label
                                            htmlFor="tps-suburb"
                                            className="block text-sm font-semibold text-tp-forest mb-1"
                                        >
                                            Suburb or postcode
                                        </label>
                                        <input
                                            id="tps-suburb"
                                            data-testid="quote-form-field-suburb"
                                            type="text"
                                            autoComplete="address-level2"
                                            value={form.suburb}
                                            onChange={(e) => setField("suburb", e.target.value)}
                                            className={`w-full h-11 rounded-lg border ${
                                                errors.suburb
                                                    ? "border-red-500"
                                                    : "border-tp-forest/20"
                                            } bg-white px-3 text-sm text-tp-ink focus:outline-none focus:ring-2 focus:ring-tp-gold focus:border-tp-gold`}
                                            placeholder="e.g. Marrickville / 2204"
                                            aria-invalid={!!errors.suburb}
                                            aria-describedby={errors.suburb ? "tps-suburb-err" : undefined}
                                        />
                                        {errors.suburb && (
                                            <p id="tps-suburb-err" className="mt-1 text-xs text-red-600">
                                                {errors.suburb}
                                            </p>
                                        )}
                                    </div>

                                    {/* Bin size */}
                                    <div className="sm:col-span-1">
                                        <label className="block text-sm font-semibold text-tp-forest mb-1">
                                            Bin size
                                        </label>
                                        <Select
                                            value={form.binSize}
                                            onValueChange={(v) => setField("binSize", v)}
                                        >
                                            <SelectTrigger
                                                data-testid="quote-form-field-binSize"
                                                className={`h-11 rounded-lg bg-white ${
                                                    errors.binSize
                                                        ? "border-red-500"
                                                        : "border-tp-forest/20"
                                                } focus:ring-2 focus:ring-tp-gold`}
                                                aria-invalid={!!errors.binSize}
                                            >
                                                <SelectValue placeholder="Choose a bin size" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {BIN_OPTIONS.map((o) => (
                                                    <SelectItem
                                                        key={o.value}
                                                        value={o.value}
                                                        data-testid={`quote-form-binSize-${o.value}`}
                                                    >
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.binSize && (
                                            <p className="mt-1 text-xs text-red-600">{errors.binSize}</p>
                                        )}
                                    </div>

                                    {/* Waste type */}
                                    <div className="sm:col-span-1">
                                        <label className="block text-sm font-semibold text-tp-forest mb-1">
                                            Type of waste
                                        </label>
                                        <Select
                                            value={form.wasteType}
                                            onValueChange={(v) => setField("wasteType", v)}
                                        >
                                            <SelectTrigger
                                                data-testid="quote-form-field-wasteType"
                                                className={`h-11 rounded-lg bg-white ${
                                                    errors.wasteType
                                                        ? "border-red-500"
                                                        : "border-tp-forest/20"
                                                } focus:ring-2 focus:ring-tp-gold`}
                                                aria-invalid={!!errors.wasteType}
                                            >
                                                <SelectValue placeholder="Choose waste type" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {WASTE_OPTIONS.map((o) => (
                                                    <SelectItem
                                                        key={o.value}
                                                        value={o.value}
                                                        data-testid={`quote-form-wasteType-${o.value.replace(/\s+/g, "-")}`}
                                                    >
                                                        {o.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        {errors.wasteType && (
                                            <p className="mt-1 text-xs text-red-600">{errors.wasteType}</p>
                                        )}
                                    </div>

                                    {/* Preferred date */}
                                    <div className="sm:col-span-1">
                                        <label className="block text-sm font-semibold text-tp-forest mb-1">
                                            Preferred delivery date{" "}
                                            <span className="text-tp-ink/50 font-normal">(optional)</span>
                                        </label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    data-testid="quote-form-field-preferredDate"
                                                    className="w-full h-11 rounded-lg border border-tp-forest/20 bg-white px-3 text-sm text-tp-ink focus:outline-none focus:ring-2 focus:ring-tp-gold flex items-center justify-between"
                                                >
                                                    <span className={form.preferredDate ? "" : "text-tp-ink/50"}>
                                                        {form.preferredDate
                                                            ? format(form.preferredDate, "EEE d MMM yyyy")
                                                            : "Pick a date"}
                                                    </span>
                                                    <CalendarDays size={16} className="text-tp-forest" />
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent align="start" className="p-0 w-auto">
                                                <Calendar
                                                    mode="single"
                                                    selected={form.preferredDate}
                                                    onSelect={(d) => setField("preferredDate", d)}
                                                    disabled={(d) => {
                                                        const today = new Date();
                                                        today.setHours(0, 0, 0, 0);
                                                        return d < today;
                                                    }}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>

                                    {/* Message */}
                                    <div className="sm:col-span-2">
                                        <label
                                            htmlFor="tps-message"
                                            className="block text-sm font-semibold text-tp-forest mb-1"
                                        >
                                            Message <span className="text-tp-ink/50 font-normal">(optional)</span>
                                        </label>
                                        <textarea
                                            id="tps-message"
                                            data-testid="quote-form-field-message"
                                            rows={4}
                                            value={form.message}
                                            onChange={(e) => setField("message", e.target.value)}
                                            className="w-full rounded-lg border border-tp-forest/20 bg-white px-3 py-2 text-sm text-tp-ink focus:outline-none focus:ring-2 focus:ring-tp-gold focus:border-tp-gold"
                                            placeholder="Anything else we should know? Access notes, timing, best contact hours..."
                                        />
                                    </div>

                                    {/* Submit + Call */}
                                    <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 pt-1">
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="quote-form-submit-button"
                                            className="tp-btn-primary h-12 px-5 text-base justify-center flex-1 disabled:opacity-70 disabled:cursor-not-allowed tp-focus"
                                        >
                                            {submitting ? (
                                                <>
                                                    <Loader2 size={18} className="animate-spin" />
                                                    Sending…
                                                </>
                                            ) : (
                                                <>
                                                    <Send size={18} strokeWidth={2.4} />
                                                    Submit enquiry
                                                </>
                                            )}
                                        </button>
                                        <a
                                            href={PHONE_HREF}
                                            data-testid="quote-form-inline-call-button"
                                            className="tp-btn-ghost h-12 px-5 text-base justify-center border border-tp-forest/15 tp-focus sm:w-auto"
                                        >
                                            <Phone size={18} strokeWidth={2.6} />
                                            Or call {PHONE_DISPLAY}
                                        </a>
                                    </div>
                                    <p className="sm:col-span-2 text-xs text-tp-ink/60">
                                        We’ll only use your details to reply to your enquiry.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default QuoteForm;
